import { interactiveFetch } from '../api';

class DashboardQueryBatcher {
    constructor() {
        this.batches = {}; // Map of dashboard_id -> { widgets: {}, promises: {}, timer: null }
        this.BATCH_WINDOW_MS = 50; 
    }

    async fetch(dashboardId, widgetId, payload, signal) {
        // Fallback to single query if no dashboard context
        if (!dashboardId) {
            return interactiveFetch('/dashboards/query', null, {
                method: 'POST',
                body: JSON.stringify(payload),
                signal
            });
        }

        return new Promise((resolve, reject) => {
            if (!this.batches[dashboardId]) {
                this.batches[dashboardId] = {
                    widgets: {},
                    promises: {},
                    timer: null,
                    signal: null
                };
            }

            const batch = this.batches[dashboardId];
            batch.widgets[widgetId] = payload;
            batch.promises[widgetId] = { resolve, reject };
            batch.signal = signal;

            if (batch.timer) {
                clearTimeout(batch.timer);
            }

            batch.timer = setTimeout(() => this.flush(dashboardId), this.BATCH_WINDOW_MS);
        });
    }

    async flush(dashboardId) {
        const batch = this.batches[dashboardId];
        delete this.batches[dashboardId];

        try {
            const batchPayload = {
                widgets: batch.widgets
            };
            
            const result = await interactiveFetch(`/dashboards/${dashboardId}/query-all`, null, {
                method: 'POST',
                body: JSON.stringify(batchPayload),
                signal: batch.signal // Might cancel entire batch if last widget unmounts, but usually fine
            });

            const resultsMap = result?.results || {};
            for (const [widgetId, promisePair] of Object.entries(batch.promises)) {
                if (resultsMap[widgetId]) {
                    if (resultsMap[widgetId].error) {
                        promisePair.reject(new Error(resultsMap[widgetId].error));
                    } else {
                        promisePair.resolve(resultsMap[widgetId]);
                    }
                } else {
                    promisePair.resolve({ data: [], meta: { columns: [] } });
                }
            }
        } catch (error) {
            // Reject all if the batch API fails entirely
            for (const promisePair of Object.values(batch.promises)) {
                promisePair.reject(error);
            }
        }
    }
}

export const queryBatcher = new DashboardQueryBatcher();
